﻿import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useMeQuery } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";

/**
 * Auth gate — eski Next.js middleware.ts'in client-side karşılığı.
 *
 * Backend session cookie'si **HttpOnly** olarak set ediliyor, dolayısıyla
 * `document.cookie`'den göremeyiz. Auth durumunu `GET /organizers/me` ile
 * doğruluyoruz; 401 → login'e redirect, 200 → outlet.
 *
 * Login mutation `onSuccess` cache'i zaten dolduruyor → cache hit'te query
 * atılmaz, redirect gecikmesi minimum olur.
 */
export function ProtectedRoute() {
  const location = useLocation();
  const { data, isLoading, error, isFetching } = useMeQuery();

  if (data) return <Outlet />;

  if (isLoading || (isFetching && !error)) {
    return <FullPageSpinner />;
  }

  const search =
    location.pathname !== "/" ? `?from=${encodeURIComponent(location.pathname)}` : "";
  return <Navigate to={`/login${search}`} replace />;
}

/**
 * Auth sayfaları için ters guard: oturumlu kullanıcı login/signup'a düşmesin.
 * `allowAuthenticated` true ise (verify-email, check-email gibi geçiş sayfaları)
 * auth durumu ne olursa olsun render eder.
 */
export function PublicAuthRoute({ allowAuthenticated = false }: { allowAuthenticated?: boolean }) {
  const { data, isLoading, error } = useMeQuery();

  if (allowAuthenticated) return <Outlet />;

  if (isLoading) return <FullPageSpinner />;

  if (data) return <Navigate to="/" replace />;

  // 401 veya başka hata → public sayfayı göster (login formu vs.).
  if (error instanceof ApiError && error.status === 401) return <Outlet />;
  return <Outlet />;
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/20 border-t-foreground" />
    </div>
  );
}
